import type { ReactNode } from 'react';
import { seenBy } from '@avslack/shared';
import { useReadPositions } from '../data/reads';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { Icon } from './Icon';

/**
 * Az uzenet allapota: kuldes -> elkuldve -> latta.
 *
 * **Csak a SAJAT uzeneteinken jelenik meg.** Masok uzeneterol tudni, hogy en
 * elolvastam-e, nekem semmit nem mond - a kuldonek szol.
 *
 * A megjelenites SZANDEKOSAN mas ket helyen:
 *
 *  - **DM-ben** uzenetenkent, profilkepekkel: ott ket-harom ember beszelget, es
 *    a visszajelzes ugyanolyan resze a parbeszednek, mint maga az uzenet.
 *  - **Csatornaban** csak az UTOLSO sajat uzenet alatt, es csak profilkepekkel.
 *    Egy harminc fos csatornaban a nevsor folyamatos megfigyeles-erzetet
 *    keltene, uzenetenkent kiirva pedig elnyomna magat a beszelgetest.
 *
 * A nevsor a szamon HOVERRE viszont ott van - ket kulonbozo dologrol van szo.
 * Ami ALLANDOAN a kepernyon all, az megfigyeles-erzetet kelt; amiert oda kell
 * vinni az egeret, az egy kerdesre adott valasz. A "ki latta mar?" pedig
 * valodi kerdes: ebbol derul ki, hogy erdemes-e meg varni valakire.
 */

/** Ennyi nevet irunk ki; a tobbi osszevonva. Egy harminc neves buborek olvashatatlan. */
const NEVSOR_MAX = 12;

/**
 * A latok nevsora, betűrendben.
 *
 * A `seenBy` a poziciok bejarasi sorrendjeben ad vissza, ami a felhasznalo
 * szamara veletlenszeru - egy nevsorban viszont keresni szoktunk, nem olvasni.
 */
function nevsor(readers: string[], nameOf: (id: string, fallback: string) => string): string {
  const nevek = readers.map((id) => nameOf(id, 'Valaki')).sort((a, b) => a.localeCompare(b, 'hu'));

  if (nevek.length <= NEVSOR_MAX) return nevek.join(', ');
  return `${nevek.slice(0, NEVSOR_MAX).join(', ')} és még ${nevek.length - NEVSOR_MAX} fő`;
}
export function MessageStatus({
  channelId,
  seq,
  senderId,
  pending,
  failed,
  isDirect,
  isLastOwn,
}: {
  channelId: string;
  seq: number;
  senderId: string;
  pending: boolean | undefined;
  failed: boolean | undefined;
  /** DM vagy csoportos DM - ott nevvel es uzenetenkent mutatjuk. */
  isDirect: boolean;
  /** Csatornaban csak az utolso sajat uzenet ala kerul a jelzes. */
  isLastOwn: boolean;
}) {
  const { data: positions } = useReadPositions(channelId);
  const { nameOf } = useUsers();

  // A sikertelen kuldesnek sajat, hangosabb jelzese van (lasd MessageRow).
  if (failed) return null;

  if (pending) {
    return (
      <Status title="Küldés folyamatban">
        <Icon name="ora" size="sm" /> küldés…
      </Status>
    );
  }

  const readers = seenBy(positions, seq, senderId);

  if (!isDirect) {
    // Csatorna: csak az utolso sajat uzenet alatt, es csak szammal.
    if (!isLastOwn || readers.length === 0) return null;
    return (
      <Status title={nevsor(readers, nameOf)} names={nevsor(readers, nameOf)}>
        <ReaderAvatars readers={readers} />
      </Status>
    );
  }

  if (readers.length === 0) {
    return (
      <Status title="Elküldve, még nem olvasták">
        <Icon name="pipa" size="sm" /> elküldve
      </Status>
    );
  }

  /*
    DM-ben is profilkepekkel (mint a csatornaban), de itt uzenetenkent, a dupla
    pipa mellett: a kep a "latta" - a nev hoverre es a kepernyoolvasonak marad.
    Csoportos DM-ben a kepek a nevsor hosszu szovege helyett kis helyen mondjak
    el ugyanazt.
  */
  return (
    <Status title={`${nevsor(readers, nameOf)} látta`} names={nevsor(readers, nameOf)}>
      <span className="text-accent">
        <Icon name="pipa" size="sm" />
        <Icon name="pipa" size="sm" />
      </span>
      <ReaderAvatars readers={readers} />
    </Status>
  );
}

/**
 * Csatornában a „látta" kis, egymásra csúsztatott profilképek.
 *
 * A fősor kicsi marad (a kép 16 px), a felesleg „+N". A teljes névsor
 * ugyanúgy a hoverön (és a képernyőolvasónak) van – lásd a fenti megjegyzést.
 */
const KEPEK_MAX = 5;

function ReaderAvatars({ readers }: { readers: string[] }) {
  const { usersById, nameOf } = useUsers();
  const shown = readers.slice(0, KEPEK_MAX);
  const rest = readers.length - shown.length;

  return (
    <span className="flex items-center" aria-hidden="true">
      {shown.map((id, index) => (
        <Avatar
          key={id}
          userId={id}
          name={nameOf(id, 'Valaki')}
          avatarUrl={usersById.get(id)?.avatarUrl}
          size="xs"
          className={['ring-2 ring-surface', index > 0 ? '-ml-1.5' : ''].join(' ')}
        />
      ))}
      {rest > 0 && <span className="ml-1 text-2xs text-ink-faint">+{rest}</span>}
    </span>
  );
}

function Status({
  children,
  title,
  names,
}: {
  children: ReactNode;
  title: string;
  names?: string;
}) {
  return (
    <p title={title} className="mt-0.5 flex items-center justify-end gap-1 text-2xs text-ink-faint">
      {children}
      {/* A title-t a kepernyoolvaso es az erintokepernyo NEM mutatja - a nevsor itt is elerheto. */}
      {names && <span className="sr-only">: {names}</span>}
    </p>
  );
}
